// Jump in, the sim's half (2026-11-01; jumpin.ts has the address). One builder for a bot match, used by main.ts for
// every offline match (so the landing's hero and /play make it the same way), and the rule for which corner a jump
// takes, the same on the landing (to offer it) and on /play (to take it). No DOM: tests/live-hero.test.ts holds the
// rebuilt match to the hero's hash, tick for tick.
import { Bot } from '../sim/bot';
import { FACTIONS, type FactionId } from '../sim/defs';
import { PLAYERS, STATE, Sim } from '../sim/sim';
import { hypot } from '../sim/dmath';

/** The bots of an offline match: one per seat but `skip` (yours; -1 for none), each with its army from `armies` (seat p
 *  takes the p-th, main.ts ?armies=) or, with none, its own pick from the seed. Made in seat order: each draws on the
 *  sim's Rng, so the order is part of the match. */
export function matchBots(sim: Sim, armies: readonly FactionId[], skip = -1): Bot[] {
  const bots: Bot[] = [];
  for (let p = 0; p < PLAYERS; p++) if (p !== skip) bots.push(armies.length ? new Bot(sim, p, armies[p % armies.length]) : new Bot(sim, p));
  return bots;
}

/** Only real army names, as main.ts reads ?armies=. */
export const armiesOf = (names: readonly string[]) => names.filter((f): f is FactionId => FACTIONS.includes(f as FactionId));

/** One tick of a bot match, in main.ts stepSim's order: every bot thinks in seat order, then the sim steps. */
export function stepBots(sim: Sim, bots: readonly Bot[], dt: number) {
  for (const b of bots) b.tick(dt);
  sim.step();
}

/** The corner still in the match with the most soldiers standing, then the most base left, then the lowest seat; null
 *  once the match is decided. */
export function strongestSeat(sim: Sim): number | null {
  if (sim.winner >= 0) return null;
  let best: number | null = null;
  for (let p = 0; p < PLAYERS; p++) {
    const q = sim.players[p];
    if (q.out) continue;
    const b = best === null ? null : sim.players[best];
    if (!b || q.alive > b.alive || (q.alive === b.alive && q.baseHp > b.baseHp)) best = p;
  }
  return best;
}

/** How near the camera's look point a soldier counts as the one it is filming (about the hero's view, half across). */
export const VIEW_R = 28;

/** The corner the hero's camera is on: the most of its soldiers within VIEW_R of (x, z), among corners still in; none
 *  there and it is the strongest. */
export function seatInView(sim: Sim, x: number, z: number): number | null {
  if (sim.winner >= 0) return null;
  const n = new Array<number>(PLAYERS).fill(0);
  for (let i = 0; i < sim.x.length; i++) {
    const t = sim.team[i];
    if (sim.state[i] === STATE.dead || t >= PLAYERS || sim.players[t].out) continue;
    if (hypot(sim.x[i] - x, sim.y[i] - z) <= VIEW_R) n[t]++;
  }
  let best = -1;
  for (let p = 0; p < PLAYERS; p++) if (n[p] > 0 && (best < 0 || n[p] > n[best])) best = p;
  return best >= 0 ? best : strongestSeat(sim);
}

/** The corner a jump takes: the one asked for while it is still in the match, else the strongest. */
export function pickSeat(sim: Sim, want: number | null): number | null {
  if (sim.winner >= 0) return null;
  return want !== null && want >= 0 && want < PLAYERS && !sim.players[want].out ? want : strongestSeat(sim);
}
